(function () {
  'use strict';

  var TEST_ID = 'MC_CART_2THRESHOLDSMYSTERYGIFT_HISTRIPS';
  var PREVIEW_STORAGE_KEY = 'maison_ab_preview';
  var previewTestId = new URLSearchParams(window.location.search).get('maison_ab_preview');
  var storedPreviewTestId = null;

  try {
    if (previewTestId === TEST_ID) {
      window.sessionStorage.setItem(PREVIEW_STORAGE_KEY, TEST_ID);
    } else if (previewTestId) {
      window.sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
    }

    storedPreviewTestId = window.sessionStorage.getItem(PREVIEW_STORAGE_KEY);
  } catch (error) {
    storedPreviewTestId = null;
  }

  if (previewTestId === TEST_ID || storedPreviewTestId === TEST_ID) {
    document.documentElement.setAttribute('data-maison-ab-preview', TEST_ID);
  }

  if (customElements.get('cart-thresholds')) return;

  // Must exactly match the discount app's line-item attribute rule.
  var GIFT_PROPERTY = '_free_Gift';
  var LEGACY_GIFT_PROPERTIES = ['_freeGift', 'free_Gift'];
  var GIFT_PROPERTY_VALUE = '120';
  var RED_LIGHT_PRODUCT_ID = '15561765486936';
  var ENABLED_PROPERTY = '--maison-cart-thresholds-enabled';
  var GIFT_SETTLE_DELAY = 650;
  var giftOperationQueue = Promise.resolve();
  var giftAddsInFlight = new Set();
  // Preserve terminal failures across the drawer's section replacements.
  var giftFailures = new Map();

  function isGiftLine(item) {
    return String(item.properties?.[GIFT_PROPERTY]) === GIFT_PROPERTY_VALUE ||
      isLegacyGiftLine(item);
  }

  function isLegacyGiftLine(item) {
    return LEGACY_GIFT_PROPERTIES.some((property) =>
      String(item?.properties?.[property]) === GIFT_PROPERTY_VALUE
    );
  }

  function enqueueGiftOperation(callback) {
    var operation = giftOperationQueue.then(callback, callback);
    giftOperationQueue = operation.catch(() => {});
    return operation;
  }

  class CartThresholds extends HTMLElement {
    static get observedAttributes() {
      return [
        'data-qualified',
        'data-gift-present',
        'data-gift-confirmed',
        'data-gift-legacy',
        'data-gift-line-key',
        'data-gift-line-variant-id',
        'data-gift-variant-id',
        'data-shipping-total',
        'data-cart-total',
        'data-shipping-threshold-base',
        'data-gift-threshold-base',
        'data-gift-threshold',
        'data-gift-threshold-is-currency-override',
        'data-variant-eligible'
      ];
    }

    constructor() {
      super();
      this._busy = false;
      this._queued = false;
      this._lastAttemptedVariantId = null;
      this._retryCount = 0;
      this._cartChangeVersion = 0;
      this._settledCartRequested = false;
      this._onCartChanged = this._onCartChanged.bind(this);
    }

    connectedCallback() {
      document.addEventListener('cart:change', this._onCartChanged);

      this._documentObserver = new MutationObserver(() => this._queueSync());
      this._documentObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      if (document.body) {
        this._bodyObserver = new MutationObserver(() => this._queueSync());
        this._bodyObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }

      if (document.head) {
        this._headObserver = new MutationObserver((mutations) => {
          this._queueSync();

          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType !== Node.ELEMENT_NODE || !node.matches('link[rel="stylesheet"]')) return;
              node.addEventListener('load', () => this._queueSync(), { once: true });
            });
          });
        });
        this._headObserver.observe(document.head, { childList: true, subtree: true });
      }

      this._applyThresholdState();
      this._queueSync();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChanged);
      this._documentObserver?.disconnect();
      this._bodyObserver?.disconnect();
      this._headObserver?.disconnect();
      if (this._thresholdFrame) cancelAnimationFrame(this._thresholdFrame);
      window.clearTimeout(this._syncTimer);
      window.clearTimeout(this._retryTimer);
    }

    attributeChangedCallback(name, previousValue, currentValue) {
      if (name === 'data-qualified' && currentValue !== 'true') {
        this._lastAttemptedVariantId = null;
        this._retryCount = 0;
      }

      if (previousValue !== currentValue && this.isConnected) {
        if (name !== 'data-qualified' && name !== 'data-gift-present' && name !== 'data-gift-line-key') {
          this._queueThresholdState();
        }
        this._queueSync();
      }
    }

    _queueThresholdState() {
      if (!this.isConnected) return;
      if (this._thresholdFrame) cancelAnimationFrame(this._thresholdFrame);
      this._thresholdFrame = requestAnimationFrame(() => this._applyThresholdState());
    }

    _queueSync() {
      if (!this.isConnected) return;
      window.clearTimeout(this._syncTimer);
      this._queued = true;
      this._syncTimer = window.setTimeout(() => {
        this._queued = false;
        this._syncGift();
      }, GIFT_SETTLE_DELAY);
    }

    _onCartChanged(event) {
      var cart = event.detail?.cart;
      if (!cart || !Array.isArray(cart.items)) return;

      this._cartChangeVersion += 1;
      this._settledCartRequested = true;
      this._applyCartSnapshot(cart);
      this._queueSync();
    }

    _applyCartSnapshot(cart) {
      var giftLine = cart.items.find(isGiftLine);
      var qualifyingTotal = cart.items.reduce((total, item) => {
        if (isGiftLine(item)) return total;
        return total + item.final_line_price;
      }, 0);
      var firstCartProduct = [...cart.items].reverse().find((item) =>
        !isGiftLine(item)
      );
      var variantEligible = String(firstCartProduct?.product_id || '') !== RED_LIGHT_PRODUCT_ID;

      this.dataset.shippingTotal = String(Number(cart.total_price) || 0);
      this.dataset.cartTotal = String(qualifyingTotal);
      this.dataset.giftPresent = String(Boolean(giftLine));
      this.dataset.giftLegacy = String(isLegacyGiftLine(giftLine));
      this.dataset.giftLineKey = giftLine?.key || '';
      this.dataset.giftLineVariantId = String(giftLine?.variant_id || giftLine?.id || '');
      this.dataset.giftConfirmed = String(Boolean(giftLine && giftLine.final_line_price === 0 &&
        String(giftLine.variant_id || giftLine.id) === this.dataset.giftVariantId));
      this.dataset.variantEligible = String(variantEligible);
      this.closest('#cart-drawer')
        ?.querySelector('.cart-thresholds__control')
        ?.setAttribute('data-variant-eligible', String(variantEligible));

      this._applyThresholdState();
    }

    _experimentIsActive() {
      return getComputedStyle(this).getPropertyValue(ENABLED_PROPERTY).trim() === '1';
    }

    _formatMoney(cents) {
      var format = window.themeVariables?.settings?.moneyFormat || '{{amount}}';
      var placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

      if (!placeholder) {
        return new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD'
        }).format(cents / 100);
      }

      var precision = placeholder[1].includes('no_decimals') ? 0 : 2;
      var thousands = placeholder[1].includes('apostrophe') ? "'" : ',';
      var decimal = placeholder[1].includes('comma_separator') ? ',' : '.';
      if (decimal === ',') thousands = '.';

      var amount = (cents / 100).toFixed(precision);
      var parts = amount.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      amount = precision > 0 ? parts[0] + decimal + parts[1] : parts[0];

      return format.replace(placeholder[0], amount);
    }

    _applyThresholdState() {
      var shippingThreshold = Number(this.dataset.shippingThresholdBase) || 0;
      var giftThreshold = Number(this.dataset.giftThreshold) || Number(this.dataset.giftThresholdBase) || 0;
      var shippingTotal = Number(this.dataset.shippingTotal) || 0;
      var cartTotal = Number(this.dataset.cartTotal) || 0;

      if (shippingThreshold <= 0 || giftThreshold <= shippingThreshold) return;

      var shippingReached = shippingTotal >= shippingThreshold;
      var giftReached = cartTotal >= giftThreshold;
      var shippingProgress = Math.min(1, shippingTotal / shippingThreshold);
      var giftProgress = cartTotal <= shippingThreshold
        ? 0
        : Math.min(1, (cartTotal - shippingThreshold) / (giftThreshold - shippingThreshold));

      this.dataset.resolvedGiftThreshold = String(giftThreshold);
      this.dataset.qualified = String(giftReached);

      this._updateMilestone('shipping', shippingReached, shippingProgress, 0, shippingThreshold, shippingTotal);
      this._updateMilestone('gift', giftReached, giftProgress, shippingThreshold, giftThreshold, cartTotal);

      this._updateGiftMessage();
    }

    _updateGiftMessage() {
      var cartTotal = Number(this.dataset.cartTotal) || 0;
      var giftThreshold = Number(this.dataset.resolvedGiftThreshold) || 0;
      var qualified = this.dataset.qualified === 'true';
      var failureKey = this.dataset.giftVariantId;
      if (!qualified || giftFailures.get(failureKey) !== cartTotal) giftFailures.delete(failureKey);
      var confirmed = this.dataset.giftConfirmed === 'true';
      var failed = giftFailures.has(failureKey);
      var adding = qualified && !confirmed && !failed && this._experimentIsActive();
      var state = !qualified ? 'unreached' : confirmed ? 'added' : failed ? 'error' : adding ? 'adding' : 'unreached';
      this.dataset.giftStatus = state;
      var message = this.dataset.giftLabel || 'Mystery gift';
      if (state === 'adding') message = this.dataset.addingMessage || 'Adding Gift…';
      if (state === 'added') message = this.dataset.addedMessage || 'Mystery gift added!';
      if (state === 'error') message = 'Gift could not be added';
      var giftLabel = this.querySelector('.cart-thresholds__gift-label-text');
      if (giftLabel) giftLabel.textContent = message;

      var title = qualified ? this.dataset.reachedMessage : this.dataset.unreachedMessage;
      var remaining = Math.max(0, giftThreshold - cartTotal);
      var shippingThreshold = Number(this.dataset.shippingThresholdBase) || 0;
      var shippingTotal = Number(this.dataset.shippingTotal) || 0;
      if (shippingTotal < shippingThreshold) {
        title = this.dataset.shippingUnreachedMessage || 'Spend [amount] more and get free shipping!';
        remaining = shippingThreshold - shippingTotal;
      }
      var messageElement = this.querySelector('.cart-thresholds__message');

      if (messageElement && title) {
        messageElement.textContent = title
          .split('[amount]').join(this._formatMoney(remaining))
          .split('[value]').join(this.dataset.giftValue || '');
      }
    }

    _updateMilestone(step, reached, progress, minimum, maximum, current) {
      var milestone = this.querySelector(`[data-threshold-step="${step}"]`);
      if (!milestone) return;

      milestone.classList.toggle('is-locked', !reached);

      var rail = milestone.querySelector('[role="progressbar"]');
      if (rail) {
        rail.setAttribute('aria-valuemin', String(minimum));
        rail.setAttribute('aria-valuemax', String(maximum));
        rail.setAttribute('aria-valuenow', String(Math.min(maximum, Math.max(minimum, current))));
      }

      milestone.querySelector('.cart-thresholds__fill')?.style.setProperty('--cart-threshold-progress', progress);
    }

    async _syncGift(cartSnapshot) {
      if (this._busy) return;
      this._updateGiftMessage();

      var settledCart = cartSnapshot;

      if ((!settledCart || !Array.isArray(settledCart.items)) && this._settledCartRequested) {
        var requestedCartChangeVersion = this._cartChangeVersion;

        try {
          var settledCartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
            headers: { Accept: 'application/json' }
          });
          settledCart = await settledCartResponse.json();

          if (!settledCartResponse.ok || !Array.isArray(settledCart.items)) {
            throw new Error('Unable to read the settled cart.');
          }

          if (requestedCartChangeVersion !== this._cartChangeVersion) return;

          this._settledCartRequested = false;
          this._applyCartSnapshot(settledCart);
        } catch (error) {
          console.error('[Maison cart thresholds]', error);
          giftFailures.set(this.dataset.giftVariantId, Number(this.dataset.cartTotal) || 0);
          this._updateGiftMessage();
          return;
        }
      }

      var active = this._experimentIsActive();
      var qualified = this.dataset.qualified === 'true';
      var giftPresent = this.dataset.giftPresent === 'true';
      var variantId = this.dataset.giftVariantId;
      var giftLineKey = this.dataset.giftLineKey;
      var giftLineVariantId = this.dataset.giftLineVariantId;

      // Replace gifts using either previous attribute spelling with the discount rule attribute.
      if (active && qualified && giftPresent && giftLineKey && this.dataset.giftLegacy === 'true') {
        await enqueueGiftOperation(() => this._removeGift(giftLineKey));
        return;
      }

      if (active && qualified && giftPresent && giftLineKey && giftLineVariantId && String(giftLineVariantId) !== String(variantId)) {
        await enqueueGiftOperation(() => this._removeGift(giftLineKey));
        return;
      }

      if ((!active || !qualified) && giftPresent && giftLineKey) {
        await enqueueGiftOperation(() => this._removeGift(giftLineKey));
        return;
      }

      if (!active || !qualified || giftPresent || !variantId || giftFailures.has(variantId) || this._lastAttemptedVariantId === variantId || giftAddsInFlight.has(variantId)) {
        return;
      }

      this._lastAttemptedVariantId = variantId;
      giftAddsInFlight.add(variantId);

      try {
        await enqueueGiftOperation(() => this._addGift(variantId, settledCart));
      } finally {
        giftAddsInFlight.delete(variantId);
      }
    }

    async _addGift(variantId, cartSnapshot) {
      this._busy = true;
      this.setAttribute('aria-busy', 'true');

      try {
        var existingCart = cartSnapshot;

        if (!existingCart || !Array.isArray(existingCart.items)) {
          var existingCartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
            headers: { Accept: 'application/json' }
          });
          existingCart = await existingCartResponse.json();
        }

        var existingGiftLine = existingCart?.items?.find((item) =>
          String(item.variant_id || item.id) === String(variantId) &&
          isGiftLine(item)
        );
        var qualifyingTotal = existingCart.items.reduce((total, item) => {
          if (isGiftLine(item)) return total;
          return total + item.final_line_price;
        }, 0);
        var resolvedGiftThreshold = Number(this.dataset.resolvedGiftThreshold) || 0;

        if (!this.isConnected || !this._experimentIsActive()) return;

        if (existingGiftLine) {
          if (existingGiftLine.final_line_price !== 0) {
            await this._changeGiftQuantity(existingGiftLine.key, 0);
            throw new Error('The mystery gift discount is not active; the charged item was removed from the cart.');
          }

          this.dataset.giftPresent = 'true';
          this.dataset.giftConfirmed = 'true';
          this.dataset.giftLineKey = existingGiftLine.key;
          this._updateGiftMessage();
          this._retryCount = 0;
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        if (resolvedGiftThreshold > 0 && qualifyingTotal < resolvedGiftThreshold) {
          this.dataset.qualified = 'false';
          this._lastAttemptedVariantId = null;
          return;
        }

        var sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        var addResponse = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: Number(variantId),
              quantity: 1,
              properties: { [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE }
            }],
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        });

        var addResult = await addResponse.json();
        if (!addResponse.ok || addResult.status) {
          throw new Error(addResult.description || addResult.message || 'Unable to add the mystery gift.');
        }

        var addedGiftItems = Array.isArray(addResult.items) ? addResult.items : [addResult];
        var giftLine = addedGiftItems.find((item) =>
          String(item.variant_id || item.id) === String(variantId) &&
          String(item.properties?.[GIFT_PROPERTY]) === GIFT_PROPERTY_VALUE
        );

        if (!giftLine) {
          throw new Error('The mystery gift was not returned in the cart.');
        }

        if (giftLine.final_line_price !== 0) {
          await this._changeGiftQuantity(giftLine.key, 0);
          throw new Error('The mystery gift discount is not active; the charged item was removed from the cart.');
        }

        this.dataset.giftPresent = 'true';
        this.dataset.giftConfirmed = 'true';
        this.dataset.giftLineKey = giftLine.key;
        this._updateGiftMessage();
        this._retryCount = 0;

        var hasBundledSections = sectionsToBundle.length > 0 && sectionsToBundle.every((sectionId) =>
          typeof addResult.sections?.[sectionId] === 'string'
        );

        if (hasBundledSections) {
          var cart = {
            ...existingCart,
            item_count: (Number(existingCart.item_count) || 0) + (Number(giftLine.quantity) || 1),
            items: [giftLine, ...existingCart.items],
            sections: addResult.sections,
            total_price: (Number(existingCart.total_price) || 0) + (Number(giftLine.final_line_price) || 0)
          };

          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cart
            }
          }));
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (error) {
        console.error('[Maison cart thresholds]', error);

        var discountMissing = error.message?.includes('discount is not active');
        if (!discountMissing && this._retryCount < 2 && this.isConnected) {
          this._retryCount += 1;
          this._lastAttemptedVariantId = null;
          this._retryTimer = window.setTimeout(() => this._queueSync(), 750);
        } else {
          giftFailures.set(variantId, Number(this.dataset.cartTotal) || 0);
          this._updateGiftMessage();
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } finally {
        this._busy = false;
        this.removeAttribute('aria-busy');
      }
    }

    async _removeGift(lineKey) {
      this._busy = true;
      this.setAttribute('aria-busy', 'true');

      try {
        var sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        var cart = await this._changeGiftQuantity(lineKey, 0, sectionsToBundle);
        this.dataset.giftPresent = 'false';
        this.dataset.giftConfirmed = 'false';
        this.dataset.giftLegacy = 'false';
        this.dataset.giftLineKey = '';
        this.dataset.giftLineVariantId = '';

        var hasBundledSections = sectionsToBundle.length > 0 && sectionsToBundle.every((sectionId) =>
          typeof cart.sections?.[sectionId] === 'string'
        );

        if (hasBundledSections) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'gift:sync',
              cart: cart
            }
          }));
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (error) {
        console.error('[Maison cart thresholds]', error);
      } finally {
        this._busy = false;
        this.removeAttribute('aria-busy');
      }
    }

    async _changeGiftQuantity(lineKey, quantity, sectionsToBundle) {
      var body = { id: lineKey, quantity: quantity };

      if (Array.isArray(sectionsToBundle) && sectionsToBundle.length > 0) {
        body.sections = sectionsToBundle.join(',');
        body.sections_url = window.location.pathname;
      }

      var response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });
      var result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to update the mystery gift.');
      }

      return result;
    }
  }

  customElements.define('cart-thresholds', CartThresholds);
})();
